import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { hotels, zonePricing, roundTripMultiplier } from "@/pages/hotelsData";

const CUN = "Cancun International Airport (CUN)";

const svcLabels = {
  en: {
    service: "Service type",
    from: "From",
    to: "To",
    passengers: "Passengers",
    currency: "Currency",
    reserve: "Reserve",
    fromAirport: "Airport → Hotel (One-way)",
    roundTrip: "Airport ↔ Hotel (Round-trip)",
    tour: "Private Tour",
    fromCUN: "Cancún International Airport",
    toPlaceholder: "Type your hotel or destination",
    quote: "Estimated",
    noResults: "No hotels match",
  },
  es: {
    service: "Tipo de servicio",
    from: "Desde",
    to: "Hacia",
    passengers: "Pasajeros",
    currency: "Moneda",
    reserve: "Reservar",
    fromAirport: "Aeropuerto → Hotel (Sencillo)",
    roundTrip: "Aeropuerto ↔ Hotel (Redondo)",
    tour: "Tour privado",
    fromCUN: "Aeropuerto Internacional de Cancún",
    toPlaceholder: "Escribe tu hotel o destino",
    quote: "Estimado",
    noResults: "Sin coincidencias",
  },
};

const QuickSearchBar = () => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const labels = svcLabels[lang];

  const [service, setService] = useState("one-way");
  const [toValue, setToValue] = useState("");
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [passengers, setPassengers] = useState(2);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (suggestionRef.current && !suggestionRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const suggestions = useMemo(() => {
    const q = toValue.trim().toLowerCase();
    if (!q) return [];
    return hotels
      .filter((h) => {
        const zoneLabel = (zonePricing[h.zone]?.label[lang] || "").toLowerCase();
        return h.name.toLowerCase().includes(q) || zoneLabel.includes(q);
      })
      .slice(0, 8);
  }, [toValue, lang]);

  const estimatedPrice = useMemo(() => {
    if (!selectedHotel) return null;
    const zone = zonePricing[selectedHotel.zone];
    if (!zone) return null;
    const base = zone.suv;
    return service === "round-trip" ? Math.round(base * roundTripMultiplier) : base;
  }, [selectedHotel, service]);

  const handleSelectHotel = (h) => {
    setSelectedHotel(h);
    setToValue(h.name);
    setShowSuggestions(false);
  };

  const handleReserve = () => {
    if (!selectedHotel) {
      const scrollTarget = document.getElementById("booking");
      if (scrollTarget) scrollTarget.scrollIntoView({ behavior: "smooth" });
      return;
    }
    const zone = zonePricing[selectedHotel.zone];
    const svc = service === "tour" ? "tour" : service;
    try {
      sessionStorage.setItem(
        "hrm_prefill",
        JSON.stringify({
          service_type: svc,
          vehicle_type: "suv",
          pickup_location: CUN,
          dropoff_location: `${selectedHotel.name} — ${zone.label[lang]}`,
          passengers,
          estimated_price: estimatedPrice,
        })
      );
      window.dispatchEvent(new CustomEvent("hrm:prefill"));
    } catch (e) {
      // ignore
    }
    navigate("/#booking");
    setTimeout(() => {
      const el = document.getElementById("booking");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  return (
    <div
      data-testid="quick-search-bar"
      className="relative z-20 max-w-6xl mx-auto -mt-16 sm:-mt-24 mx-4 sm:mx-8 lg:mx-auto"
    >
      <div className="bg-[#C6A87C] shadow-2xl">
        {/* Title strip */}
        <div className="px-5 py-3 border-b border-black/10 flex flex-wrap items-baseline justify-between gap-2">
          <p className="font-serif text-lg sm:text-xl text-[#1A1F24]">
            {lang === "es" ? "Transporte en Cancún y Riviera Maya" : "Transportation Cancún & Riviera Maya"}
          </p>
          {estimatedPrice != null && (
            <p className="text-xs uppercase tracking-[0.2em] text-[#1A1F24]" data-testid="quick-estimate">
              {labels.quote}:{" "}
              <span className="font-serif text-base font-semibold">${estimatedPrice} USD</span>
            </p>
          )}
        </div>

        {/* Fields grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-black/10">
          {/* Service type */}
          <div className="md:col-span-3 bg-[#C6A87C] p-3">
            <label className="block text-[10px] uppercase tracking-[0.25em] text-[#1A1F24]/80 mb-1 font-semibold">
              {labels.service}
            </label>
            <select
              data-testid="quick-service"
              value={service}
              onChange={(e) => setService(e.target.value)}
              className="w-full bg-white text-[#1A1F24] px-3 py-2 text-sm focus:outline-none border border-transparent focus:border-[#1A1F24]"
            >
              <option value="one-way">{labels.fromAirport}</option>
              <option value="round-trip">{labels.roundTrip}</option>
              <option value="tour">{labels.tour}</option>
            </select>
          </div>

          {/* From (fixed) */}
          <div className="md:col-span-3 bg-[#C6A87C] p-3">
            <label className="block text-[10px] uppercase tracking-[0.25em] text-[#1A1F24]/80 mb-1 font-semibold">
              {labels.from}
            </label>
            <div className="w-full bg-white text-[#1A1F24] px-3 py-2 text-sm flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-[#2E4F4F] shrink-0" />
              <span className="truncate" data-testid="quick-from">{labels.fromCUN}</span>
            </div>
          </div>

          {/* To (hotel autocomplete) */}
          <div className="md:col-span-3 bg-[#C6A87C] p-3 relative" ref={suggestionRef}>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-[#1A1F24]/80 mb-1 font-semibold">
              {labels.to}
            </label>
            <input
              data-testid="quick-to"
              type="text"
              value={toValue}
              onChange={(e) => {
                setToValue(e.target.value);
                setSelectedHotel(null);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              placeholder={labels.toPlaceholder}
              className="w-full bg-white text-[#1A1F24] px-3 py-2 text-sm placeholder:text-[#4A5568] focus:outline-none border border-transparent focus:border-[#1A1F24]"
            />
            {showSuggestions && suggestions.length > 0 && (
              <ul
                data-testid="quick-suggestions"
                className="absolute left-3 right-3 top-full mt-1 bg-white border border-[#1A1F24]/10 shadow-xl max-h-72 overflow-y-auto z-30"
              >
                {suggestions.map((h) => {
                  const zone = zonePricing[h.zone];
                  return (
                    <li key={h.id}>
                      <button
                        data-testid={`quick-suggest-${h.id}`}
                        type="button"
                        onClick={() => handleSelectHotel(h)}
                        className="w-full text-left px-4 py-2 hover:bg-[#F9F6F0] flex items-center justify-between gap-3 text-sm"
                      >
                        <div className="min-w-0">
                          <p className="text-[#1A1F24] truncate">{h.name}</p>
                          <p className="text-[10px] uppercase tracking-[0.15em] text-[#4A5568] truncate">
                            {zone.label[lang]}
                          </p>
                        </div>
                        <span className="font-serif text-[#2E4F4F] shrink-0">${zone.suv}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {showSuggestions && toValue.trim() && suggestions.length === 0 && (
              <p className="absolute left-3 right-3 top-full mt-1 bg-white px-4 py-3 text-xs text-[#4A5568] shadow-xl">
                {labels.noResults}
              </p>
            )}
          </div>

          {/* Passengers */}
          <div className="md:col-span-1 bg-[#C6A87C] p-3">
            <label className="block text-[10px] uppercase tracking-[0.25em] text-[#1A1F24]/80 mb-1 font-semibold">
              {labels.passengers}
            </label>
            <input
              data-testid="quick-passengers"
              type="number"
              min={1}
              max={20}
              value={passengers}
              onChange={(e) => setPassengers(parseInt(e.target.value || "1", 10))}
              className="w-full bg-white text-[#1A1F24] px-3 py-2 text-sm focus:outline-none border border-transparent focus:border-[#1A1F24]"
            />
          </div>

          {/* Currency */}
          <div className="md:col-span-1 bg-[#C6A87C] p-3">
            <label className="block text-[10px] uppercase tracking-[0.25em] text-[#1A1F24]/80 mb-1 font-semibold">
              {labels.currency}
            </label>
            <div className="w-full bg-white text-[#1A1F24] px-3 py-2 text-sm">USD</div>
          </div>

          {/* Reserve button */}
          <div className="md:col-span-1 bg-[#C6A87C] p-3 flex items-end">
            <button
              data-testid="quick-reserve"
              onClick={handleReserve}
              className="w-full h-[38px] bg-[#1A1F24] hover:bg-[#2E4F4F] text-white text-xs uppercase tracking-[0.15em] flex items-center justify-center gap-2 transition-colors"
            >
              {labels.reserve}
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickSearchBar;
